import React, { useEffect, useRef, useState } from 'react';
import { Resolver, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import Grid from '@mui/material/Grid';
import { Box, Typography } from '@mui/material';
import { Alert, EAlertVariants } from 'components/atoms/Alert';
import { useTranslations } from 'hooks/useTranslations';
import { StyledPageTitle } from 'theme/styles';
import Breadcrumbs from 'components/atoms/Breadcrumbs';
import { ERouteLinks } from 'models/route';
import Button from 'components/atoms/Button';
import { EButtonVariants } from 'constants/Buttons';
import { useAlert } from 'models/alertContext';
import { ActionTypes } from 'state/actions/alert';
import { useNavigate } from 'react-router-dom';
import {
  getGlobalPropertyByKey,
  updateLicenceExpirationNotificationDaysBefore,
  updateLicenceRenewBeforeExpiration,
} from 'requests/globalProperty';
import { EFormInputTypes, FormInput } from 'components/molecules/FormInput';
import { Loader } from 'components/atoms/Loader';

interface IGeneralLicenceSettingsFormValues {
  licenceRenewAllowedDaysBeforeExpiration: number;
  licenceExpirationNotificationDaysBefore: number;
}

const GeneralLicenceSettingsPage = () => {
  const { t } = useTranslations();
  const navigate = useNavigate();
  const { samePageAlert, setAlert } = useAlert();
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const schema = yup.object().shape({
    licenceRenewAllowedDaysBeforeExpiration: yup
      .number()
      .integer(t('general.validation.field.onlyIntegersAllowed.label'))
      .typeError(t('general.field.validation.required'))
      .required(t('general.field.validation.required'))
      .min(1, t('general.field.validation.minValue').replace('{0}', '1'))
      .max(365, t('general.field.validation.maxValue').replace('{0}', '365')),
    licenceExpirationNotificationDaysBefore: yup
      .number()
      .integer(t('general.validation.field.onlyIntegersAllowed.label'))
      .typeError(t('general.field.validation.required'))
      .required(t('general.field.validation.required'))
      .min(1, t('general.field.validation.minValue').replace('{0}', '1'))
      .max(365, t('general.field.validation.maxValue').replace('{0}', '365')),
  });

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<IGeneralLicenceSettingsFormValues>({
    defaultValues: {
      licenceRenewAllowedDaysBeforeExpiration: 0,
      licenceExpirationNotificationDaysBefore: 0,
    },
    resolver: yupResolver(schema) as unknown as Resolver<IGeneralLicenceSettingsFormValues>,
  });

  const hasLoaded = useRef(false);

  useEffect(() => {
    if (hasLoaded.current) return;

    const loadCurrentValue = async () => {
      hasLoaded.current = true;
      setIsLoading(true);
      try {
        const result = await getGlobalPropertyByKey(
          'licence_renewal_allowed_days_before_expiration',
        );
        if (result && result.value) {
          setValue('licenceRenewAllowedDaysBeforeExpiration', parseInt(result.value, 10));
        }
        const result2 = await getGlobalPropertyByKey(
          'licence_expiration_notification_days_before',
        );
        if (result2 && result2.value) {
          setValue('licenceExpirationNotificationDaysBefore', parseInt(result2.value, 10));
        }
      } catch (error) {
        setAlert(ActionTypes.SET_SAME_PAGE_ALERT, {
          text: t('generalLicenceSettings.error.loadFailed'),
          variant: EAlertVariants.error,
        });
      }
      setIsLoading(false);
    };

    loadCurrentValue();
  }, []);

  const onSubmit = async (data: IGeneralLicenceSettingsFormValues) => {
    setIsSubmitLoading(true);
    try {
      await updateLicenceRenewBeforeExpiration(data.licenceRenewAllowedDaysBeforeExpiration);
      await updateLicenceExpirationNotificationDaysBefore(
        data.licenceExpirationNotificationDaysBefore,
      );
      setAlert(ActionTypes.SET_SAME_PAGE_ALERT, {
        text: t('generalLicenceSettings.success.text'),
      });
    } catch (error) {
      setAlert(ActionTypes.SET_SAME_PAGE_ALERT, {
        text: t('generalLicenceSettings.error.saveFailed'),
        variant: EAlertVariants.error,
      });
    } finally {
      setIsSubmitLoading(false);
    }
  };

  const handleCancel = () => {
    navigate(ERouteLinks.Administration);
  };

  return (
    <>
      <Breadcrumbs
        items={[
          { label: t('module.administration.name'), route: ERouteLinks.Administration },
          t('generalLicenceSettings.header'),
        ]}
      />
      <StyledPageTitle variant="h4">{t('generalLicenceSettings.header')}</StyledPageTitle>

      {isLoading ? (
        <Loader isLoading={true} />
      ) : (
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={1} sx={{ mt: 1 }}>
            <Grid size={{ xs: 12, md: 6, lg: 6 }}>
              <FormInput
                name="licenceRenewAllowedDaysBeforeExpiration"
                type={EFormInputTypes.number}
                control={control}
                errors={errors}
                label={t(
                  'generalLicenceSettings.licenceRenew.licenceRenewalAllowedDaysBeforeExpiration.text',
                )}
                helperText={t(
                  'generalLicenceSettings.licenceRenew.minimumDaysThreshold.helper',
                )}
                setValue={setValue}
                required
              />
            </Grid>
            <Grid size={{ xs: 12 }} sx={{ display: { xs: 'none', md: 'block' } }} />

            <Grid size={{ xs: 12, md: 6, lg: 6 }}>
              <FormInput
                name="licenceExpirationNotificationDaysBefore"
                type={EFormInputTypes.number}
                control={control}
                label={t(
                  'generalLicenceSettings.licenceRenew.licenceExpirationNotificationDaysBefore.text',
                )}
                errors={errors}
                helperText={t(
                  'generalLicenceSettings.licenceRenew.minimumDaysThreshold.helper',
                )}
                setValue={setValue}
                required
              />
            </Grid>
          </Grid>
          <Grid container columnSpacing={2.5} rowSpacing={1} sx={{ mt: 2 }}>
            <Grid size={{ xs: 12, sm: 6, md: 3, lg: 2 }}>
              <Button
                fullWidth
                label={t('general.cancel.button')}
                id="cancel"
                variant={EButtonVariants.outlined}
                onClick={handleCancel}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3, lg: 2 }}>
              <Button
                type="submit"
                fullWidth
                label={t('general.save.button')}
                id="save"
                variant={EButtonVariants.contained}
                isLoading={isSubmitLoading}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Typography variant="caption">{t('general.mandatoryField.text')}</Typography>
            </Grid>
          </Grid>
        </form>
      )}
      {samePageAlert && (
        <Box sx={{ mt: 2 }}>
          <Alert text={samePageAlert.text} variant={samePageAlert.variant} />
        </Box>
      )}
    </>
  );
};

export default GeneralLicenceSettingsPage;
